// pages/public/LandingPage.jsx
// Assessment landing page matching execspringboard.com brand
// Logo placeholder - team will replace with real SVG

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const LandingPage = () => {
  const navigate = useNavigate();
  const [showPasscode, setShowPasscode] = useState(false);
  const [passcode, setPasscode] = useState('');

  return (
    <div style={styles.page}>

      {/* Navigation */}
      <nav style={styles.nav}>
        <div style={styles.navInner}>
          {/* Logo Placeholder - Team replaces with real SVG */}
          <div style={styles.logo}>
            <span style={styles.logoExec}>EXECUTIVE</span>
            <span style={styles.logoSpring}>Springboard</span>
          </div>
          <a href="https://execspringboard.com" style={styles.navLink} target="_blank" rel="noreferrer">
            Back to main site
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroInner}>
          <div style={styles.badge}>AI-Enhanced Leadership Assessment</div>
          <h1 style={styles.heroTitle}>
            Discover exactly where you<br />stand as a leader
          </h1>
          <p style={styles.heroSubtitle}>
            The Executive Springboard Comfort Zone Assessment maps your leadership
            preferences across 8 critical dimensions and shows you exactly where
            the gaps are between how you lead and what your organization needs.
          </p>

          <div style={styles.heroActions}>
            <button
              style={styles.btnPrimary}
              onClick={() => navigate('/start')}
            >
              Start My Assessment
            </button>
            <p style={styles.heroNote}>
              Complete your leadership assessment and unlock your personalised report for $149.
            </p>
          </div>

          {/* Enterprise link */}
          {!showPasscode ? (
            <button
              style={styles.enterpriseLink}
              onClick={() => setShowPasscode(true)}
            >
              Taking this through your company? Enter your company code here
            </button>
          ) : (
            <div style={styles.passcodeBox}>
              <input
                type="text"
                placeholder="Enter company code (e.g. EXS-PLX-4872)"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value.toUpperCase())}
                style={styles.passcodeInput}
              />
              <button
                style={styles.btnAccent}
                onClick={() => {
                  if (passcode.trim()) {
                    navigate('/start', { state: { passcode: passcode.trim() } });
                  }
                }}
              >
                Continue
              </button>
              <button
                style={styles.cancelLink}
                onClick={() => { setShowPasscode(false); setPasscode(''); }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Trust Metrics */}
      <section style={styles.trustSection}>
        <div style={styles.trustGrid}>
          {[
            { number: '100+', label: 'Fortune 1000 Mentors' },
            { number: 'NPS 86', label: 'Client Satisfaction' },
            { number: '91%', label: 'Retention at 18 Months' },
            { number: '1,350%', label: 'ROI for Organizations' },
          ].map((item, i) => (
            <div key={i} style={styles.trustTile}>
              <div style={styles.trustNumber}>{item.number}</div>
              <div style={styles.trustLabel}>{item.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* What You Get Section */}
      <section style={styles.section}>
        <div style={styles.sectionInner}>
          <div style={styles.sectionTag}>What You Receive</div>
          <h2 style={styles.sectionTitle}>This is not just a survey. This is a mirror.</h2>
          <p style={styles.sectionText}>
            In 106 carefully designed questions you will map your comfort zone across
            8 dimensions that directly impact your effectiveness as a leader.
          </p>
          <div style={styles.bulletGrid}>
            {[
              'Where your leadership strengths are concentrated',
              'Where your potential derailers and comfort zone gaps exist',
              'How your preferences align or conflict with your organization\'s needs',
              'Specific areas to focus on for immediate impact',
              'A clear starting point for your development journey',
            ].map((item, i) => (
              <div key={i} style={styles.bulletItem}>
                <div style={styles.bulletDot} />
                <span style={styles.bulletText}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section style={{ ...styles.section, backgroundColor: 'var(--color-off-white)' }}>
        <div style={styles.sectionInner}>
          <div style={styles.sectionTag}>Who This Is For</div>
          <h2 style={styles.sectionTitle}>Built for leaders who take their growth seriously</h2>
          <div style={styles.cardGrid}>
            {[
              { icon: '🎯', title: 'New Role Leaders', desc: 'Executives stepping into new leadership roles who want to get off to a strong start' },
              { icon: '📈', title: 'Succession Candidates', desc: 'Leaders identified for promotion who want to accelerate their readiness' },
              { icon: '🏆', title: 'Senior Executives', desc: 'Experienced leaders looking to extend their impact across the enterprise' },
              { icon: '💡', title: 'Growth Minded Leaders', desc: 'Anyone who wants honest, data driven insight into how they lead' },
            ].map((item, i) => (
              <div key={i} style={styles.card}>
                <div style={styles.cardIcon}>{item.icon}</div>
                <h3 style={styles.cardTitle}>{item.title}</h3>
                <p style={styles.cardDesc}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section style={styles.section}>
        <div style={styles.sectionInner}>
          <div style={styles.sectionTag}>How It Works</div>
          <h2 style={styles.sectionTitle}>Four simple steps to leadership clarity</h2>
          <div style={styles.stepsRow}>
            {[
              { step: '01', title: 'Enter your details', desc: 'Just your name and email to get started' },
              { step: '02', title: 'Complete the assessment', desc: '106 questions at your own pace, save and return anytime' },
              { step: '03', title: 'Receive your snapshot report', desc: 'Your comfort zone scorecard delivered instantly' },
              { step: '04', title: 'Upgrade for full insights', desc: 'Detailed report and a 30 minute consultation for $149' },
            ].map((item, i) => (
              <div key={i} style={styles.stepItem}>
                <div style={styles.stepNumber}>{item.step}</div>
                <h3 style={styles.stepTitle}>{item.title}</h3>
                <p style={styles.stepDesc}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={styles.ctaSection}>
        <div style={styles.ctaInner}>
          <h2 style={styles.ctaTitle}>
            Your next level of leadership starts with<br />knowing where you are today
          </h2>
          <p style={styles.ctaText}>
            Complete your leadership assessment in minutes. Get your snapshot instantly.
          </p>
          <button
            style={styles.btnPrimaryLarge}
            onClick={() => navigate('/start')}
          >
            Start My Assessment
          </button>
          <p style={styles.ctaNote}>
            Full detailed report available for $149. Includes personalized findings and a 30 minute consultation.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerInner}>
          <div style={styles.logo}>
            <span style={styles.logoExec}>EXECUTIVE</span>
            <span style={styles.logoSpring}>Springboard</span>
          </div>
          <p style={styles.footerText}>
            &copy; 2026 Executive Springboard. All rights reserved. |{' '}
            <a href="https://execspringboard.com" style={styles.footerLink}>
              execspringboard.com
            </a>
          </p>
        </div>
      </footer>

    </div>
  );
};

const styles = {
  page: { fontFamily: 'var(--font-primary)', color: 'var(--color-text)' },

  // Nav
  nav: { backgroundColor: 'var(--color-primary)', padding: '0 24px', position: 'sticky', top: 0, zIndex: 100 },
  navInner: { maxWidth: 'var(--max-width)', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '70px' },
  logo: { display: 'flex', flexDirection: 'column', gap: '1px' },
  logoExec: { fontSize: '10px', fontWeight: '700', letterSpacing: '3px', color: 'var(--color-teal)', lineHeight: 1 },
  logoSpring: { fontSize: '20px', fontWeight: '700', color: '#FFFFFF', lineHeight: 1 },
  navLink: { color: 'rgba(255,255,255,0.7)', fontSize: '14px', textDecoration: 'none' },

  // Hero
  hero: { background: 'linear-gradient(135deg, #0A2342 0%, #1B3A5C 50%, #0D2E4A 100%)', padding: '80px 24px 100px' },
  heroInner: { maxWidth: '720px', margin: '0 auto', textAlign: 'center' },
  badge: { display: 'inline-block', background: 'rgba(27, 154, 170, 0.2)', color: 'var(--color-teal-light)', border: '1px solid rgba(27, 154, 170, 0.3)', borderRadius: '100px', padding: '6px 16px', fontSize: '13px', fontWeight: '500', marginBottom: '24px' },
  heroTitle: { fontSize: '48px', fontWeight: '700', color: '#FFFFFF', lineHeight: '1.2', marginBottom: '20px' },
  heroSubtitle: { fontSize: '18px', color: 'rgba(255,255,255,0.75)', lineHeight: '1.7', marginBottom: '40px' },
  heroActions: { marginBottom: '32px' },
  heroNote: { fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginTop: '12px' },
  enterpriseLink: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' },
  passcodeBox: { display: 'flex', gap: '10px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', marginTop: '8px' },
  passcodeInput: { padding: '10px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: '14px', width: '260px', outline: 'none' },
  cancelLink: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' },

  // Buttons
  btnPrimary: { display: 'inline-block', background: 'var(--color-teal)', color: '#fff', padding: '16px 36px', borderRadius: '8px', border: 'none', fontSize: '16px', fontWeight: '600', cursor: 'pointer', transition: 'background 0.2s' },
  btnPrimaryLarge: { display: 'inline-block', background: 'var(--color-teal)', color: '#fff', padding: '18px 48px', borderRadius: '8px', border: 'none', fontSize: '18px', fontWeight: '600', cursor: 'pointer' },
  btnAccent: { background: 'var(--color-teal)', color: '#fff', padding: '10px 20px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },

  // Trust
  trustSection: { background: 'var(--color-primary)', borderTop: '1px solid rgba(255,255,255,0.1)', padding: '40px 24px' },
  trustGrid: { maxWidth: 'var(--max-width)', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px' },
  trustTile: { textAlign: 'center', padding: '20px' },
  trustNumber: { fontSize: '32px', fontWeight: '700', color: 'var(--color-teal)', marginBottom: '6px' },
  trustLabel: { fontSize: '13px', color: 'rgba(255,255,255,0.6)' },

  // Sections
  section: { padding: '80px 24px', backgroundColor: '#fff' },
  sectionInner: { maxWidth: '900px', margin: '0 auto' },
  sectionTag: { fontSize: '12px', fontWeight: '600', letterSpacing: '2px', color: 'var(--color-teal)', textTransform: 'uppercase', marginBottom: '12px' },
  sectionTitle: { fontSize: '36px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '16px', lineHeight: '1.3' },
  sectionText: { fontSize: '17px', color: 'var(--color-text-light)', lineHeight: '1.7', marginBottom: '32px' },

  // Bullets
  bulletGrid: { display: 'flex', flexDirection: 'column', gap: '14px' },
  bulletItem: { display: 'flex', alignItems: 'flex-start', gap: '12px' },
  bulletDot: { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--color-teal)', marginTop: '8px', flexShrink: 0 },
  bulletText: { fontSize: '16px', color: 'var(--color-text)', lineHeight: '1.6' },

  // Cards
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' },
  card: { background: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)', padding: '28px', boxShadow: 'var(--shadow-sm)' },
  cardIcon: { fontSize: '28px', marginBottom: '12px' },
  cardTitle: { fontSize: '16px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '8px' },
  cardDesc: { fontSize: '14px', color: 'var(--color-text-light)', lineHeight: '1.6' },

  // Steps
  stepsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px' },
  stepItem: { textAlign: 'center', padding: '20px' },
  stepNumber: { fontSize: '40px', fontWeight: '800', color: 'var(--color-teal)', opacity: 0.3, marginBottom: '12px', lineHeight: 1 },
  stepTitle: { fontSize: '15px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '8px' },
  stepDesc: { fontSize: '13px', color: 'var(--color-text-light)', lineHeight: '1.6' },

  // CTA Section
  ctaSection: { background: 'linear-gradient(135deg, #0A2342, #1B3A5C)', padding: '80px 24px', textAlign: 'center' },
  ctaInner: { maxWidth: '680px', margin: '0 auto' },
  ctaTitle: { fontSize: '36px', fontWeight: '700', color: '#fff', marginBottom: '16px', lineHeight: '1.3' },
  ctaText: { fontSize: '17px', color: 'rgba(255,255,255,0.7)', marginBottom: '32px' },
  ctaNote: { fontSize: '13px', color: 'rgba(255,255,255,0.4)', marginTop: '16px' },

  // Footer
  footer: { background: 'var(--color-primary)', borderTop: '1px solid rgba(255,255,255,0.1)', padding: '32px 24px' },
  footerInner: { maxWidth: 'var(--max-width)', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' },
  footerText: { fontSize: '13px', color: 'rgba(255,255,255,0.4)' },
  footerLink: { color: 'rgba(255,255,255,0.6)', textDecoration: 'none' },
};

export default LandingPage;
